import type { PickupLocation } from '@otw/models'
import {
  Contact,
  ContactActivity,
  ContactActivityType,
  ContactOwnerId,
  ContactStage,
  type CrmActivityDomain,
  type MarketingEmailStatus,
  activityBumpsLastActivityAt,
  resolveContactMarketingEmailStatus,
  resolveContactMarketingEmailSuppressed,
} from '@otw/models/leads/contacts'
import {
  appendContactStageHistory,
  getCurrentContactStage,
} from '@otw/models/leads/contactStageHistory'
import { db } from '@otw/models/db'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addDoc, deleteDoc, doc, getDoc, getDocs, setDoc, updateDoc } from 'firebase/firestore'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { contactCompaniesQueryKey } from './useContactCompanies'
import {
  buildMergedContact,
  remapCompanyMembersForContactMerge,
  type MergeContactsFieldSources,
  type MergeContactsInput,
} from 'views/SuperAdmin/Management/LeadManagement/util/mergeContacts'
import {
  remapContactIdsAfterContactMerge,
  rematchSoldVehiclesToContact,
} from 'views/SuperAdmin/Management/LeadManagement/util/remapContactSoldLinks'

export type { MergeContactsFieldSources, MergeContactsInput } from 'views/SuperAdmin/Management/LeadManagement/util/mergeContacts'

export const contactsQueryKey = ['contacts'] as const

export interface CreateContactInput {
  name: string
  email?: string
  secondaryEmail?: string
  phone?: string
  /** ISO date string (`YYYY-MM-DD`). */
  birthday?: string
  ownerId: ContactOwnerId
  /** Inventory owner; defaults to unassigned. */
  inventoryOwnerId?: ContactOwnerId
  /** Sales stage (legacy `stage`). */
  stage?: ContactStage
  /** Inventory stage; defaults to `prospect`. */
  inventoryStage?: ContactStage
  dropoffLocation?: PickupLocation
  leadIds?: string[]
  userId?: string
  /** Optional override for the initial activity timestamp; defaults to "now". */
  lastActivityAt?: Date | null
  /** Defaults to subscribed. */
  marketingEmailStatus?: MarketingEmailStatus
  /** Defaults to not suppressed. */
  marketingEmailSuppressed?: boolean
}

export interface UpdateContactInput {
  id: string
  name: string
  email?: string
  secondaryEmail?: string
  phone?: string
  /** ISO date string (`YYYY-MM-DD`). Omit or pass empty to clear. */
  birthday?: string
  ownerId: ContactOwnerId
  /** Inventory owner. */
  inventoryOwnerId: ContactOwnerId
  /**
   * Sales stage. Omit to leave the stored sales stage / history unchanged
   * (avoids stale edit-form values overwriting a newer pipeline change).
   */
  stage?: ContactStage
  /**
   * Inventory stage. Omit to leave the stored inventory stage / history unchanged.
   */
  inventoryStage?: ContactStage
  /** `undefined` removes the stored location, otherwise replaces it. */
  dropoffLocation?: PickupLocation | undefined
  leadIds?: string[]
  /** When provided, replaces the contact's `lastActivityAt`. */
  lastActivityAt?: Date | null
  /** When provided, replaces marketing email opt-in. */
  marketingEmailStatus?: MarketingEmailStatus
  /** When provided, replaces marketing suppress. */
  marketingEmailSuppressed?: boolean
}

export interface LogContactActivityInput {
  contactId: string
  type: ContactActivityType
  /** Free-text body. For `task` activities this is the task title. */
  note: string
  /** Linked task doc id when {@link type} is `'task'`. */
  taskId?: string
  /** Linked lead doc id when {@link type} is `'lead'`. */
  leadId?: string
  /** Sales vs inventory; defaults to sales when omitted. */
  domain?: CrmActivityDomain
}

export interface LinkContactPortalUserInput {
  contactId: string
  /** Firebase Auth / users collection id for the portal client. */
  userId: string
}

const sortContactsForList = (contacts: Contact[]): Contact[] =>
  [...contacts].sort((a, b) => {
    const aMs = a.lastActivityAt?.getTime() ?? a.updatedAt.getTime()
    const bMs = b.lastActivityAt?.getTime() ?? b.updatedAt.getTime()
    return bMs - aMs
  })

type UseContactsOptions = {
  /** When false, skip the full contacts list query (mutations only). Default true. */
  fetchList?: boolean
}

export const useContacts = (options?: UseContactsOptions) => {
  const fetchList = options?.fetchList !== false
  const { userInfo } = useAuth()
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()

  const {
    data: contacts,
    isLoading,
    error,
  } = useQuery({
    queryKey: contactsQueryKey,
    queryFn: async () => {
      const snapshot = await getDocs(db.contacts)
      return sortContactsForList(snapshot.docs.map((d) => d.data()))
    },
    enabled: fetchList,
    staleTime: 1000 * 60 * 5,
  })

  const createMutation = useMutation({
    mutationFn: async (input: CreateContactInput) => {
      const uid = userInfo?.id
      if (!uid) throw new Error('You must be signed in.')
      const now = new Date()
      const initialStage = input.stage ?? 'prospect'
      const stageHistory = appendContactStageHistory([], initialStage, now, uid)
      const initialInventoryStage = input.inventoryStage ?? 'prospect'
      const inventoryStageHistory = appendContactStageHistory(
        [],
        initialInventoryStage,
        now,
        uid,
      )
      const payload: Contact = {
        id: '',
        name: input.name.trim(),
        ...(input.email?.trim() ? { email: input.email.trim() } : {}),
        ...(input.secondaryEmail?.trim()
          ? { secondaryEmail: input.secondaryEmail.trim() }
          : {}),
        ...(input.phone?.trim() ? { phone: input.phone.trim() } : {}),
        ...(input.birthday?.trim() ? { birthday: input.birthday.trim() } : {}),
        ownerId: input.ownerId ?? null,
        inventoryOwnerId: input.inventoryOwnerId ?? null,
        stage: getCurrentContactStage(stageHistory, initialStage),
        stageHistory,
        inventoryStage: getCurrentContactStage(
          inventoryStageHistory,
          initialInventoryStage,
        ),
        inventoryStageHistory,
        ...(input.dropoffLocation ? { dropoffLocation: input.dropoffLocation } : {}),
        marketingEmailStatus: input.marketingEmailStatus ?? 'subscribed',
        marketingEmailSuppressed: input.marketingEmailSuppressed === true,
        lastActivityAt: input.lastActivityAt === undefined ? now : input.lastActivityAt,
        leadIds: input.leadIds ?? [],
        activities: [],
        ...(input.userId?.trim() ? { userId: input.userId.trim() } : {}),
        createdAt: now,
        updatedAt: now,
        createdByUserId: uid,
      }
      const docRef = await addDoc(db.contacts, payload)
      return docRef.id
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactsQueryKey })
      showSuccess('Contact created')
    },
    onError: () => showError('Could not create contact'),
  })

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateContactInput) => {
      const uid = userInfo?.id
      const ref = doc(db.contacts, input.id)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Contact not found')
      const now = new Date()
      const stageHistory =
        input.stage !== undefined && input.stage !== current.stage
          ? appendContactStageHistory(
              current.stageHistory,
              input.stage,
              now,
              uid ?? undefined,
            )
          : current.stageHistory
      const currentInventoryStage = current.inventoryStage ?? 'prospect'
      const inventoryStageHistory =
        input.inventoryStage !== undefined &&
        input.inventoryStage !== currentInventoryStage
          ? appendContactStageHistory(
              current.inventoryStageHistory,
              input.inventoryStage,
              now,
              uid ?? undefined,
            )
          : current.inventoryStageHistory
      const merged: Contact = {
        ...current,
        name: input.name.trim(),
        ownerId: input.ownerId ?? null,
        inventoryOwnerId: input.inventoryOwnerId ?? null,
        stage:
          input.stage !== undefined
            ? getCurrentContactStage(stageHistory, input.stage)
            : current.stage,
        stageHistory,
        inventoryStage:
          input.inventoryStage !== undefined
            ? getCurrentContactStage(inventoryStageHistory, input.inventoryStage)
            : currentInventoryStage,
        inventoryStageHistory,
        updatedAt: now,
        ...(input.lastActivityAt !== undefined
          ? { lastActivityAt: input.lastActivityAt }
          : {}),
        ...(input.marketingEmailStatus !== undefined
          ? { marketingEmailStatus: input.marketingEmailStatus }
          : {
              marketingEmailStatus: resolveContactMarketingEmailStatus(current),
            }),
        ...(input.marketingEmailSuppressed !== undefined
          ? { marketingEmailSuppressed: input.marketingEmailSuppressed }
          : {
              marketingEmailSuppressed: resolveContactMarketingEmailSuppressed(current),
            }),
        ...(input.leadIds !== undefined ? { leadIds: [...input.leadIds] } : {}),
      }
      if (input.email?.trim()) merged.email = input.email.trim()
      else delete merged.email
      if (input.secondaryEmail?.trim()) merged.secondaryEmail = input.secondaryEmail.trim()
      else delete merged.secondaryEmail
      if (input.phone?.trim()) merged.phone = input.phone.trim()
      else delete merged.phone
      if (input.birthday?.trim()) merged.birthday = input.birthday.trim()
      else delete merged.birthday
      if (input.dropoffLocation) merged.dropoffLocation = input.dropoffLocation
      else delete merged.dropoffLocation
      await setDoc(ref, merged)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactsQueryKey })
      showSuccess('Contact updated')
    },
    onError: () => showError('Could not update contact'),
  })

  const setMarketingEmailStatusMutation = useMutation({
    mutationFn: async ({
      id,
      marketingEmailStatus,
    }: {
      id: string
      marketingEmailStatus: MarketingEmailStatus
    }) => {
      const ref = doc(db.contacts, id)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Contact not found')
      const merged: Contact = {
        ...current,
        marketingEmailStatus,
        updatedAt: new Date(),
      }
      await setDoc(ref, merged)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactsQueryKey })
      showSuccess('Marketing email status updated')
    },
    onError: () => showError('Could not update marketing email status'),
  })

  const setMarketingEmailSuppressedMutation = useMutation({
    mutationFn: async ({
      id,
      marketingEmailSuppressed,
    }: {
      id: string
      marketingEmailSuppressed: boolean
    }) => {
      const ref = doc(db.contacts, id)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Contact not found')
      const merged: Contact = {
        ...current,
        marketingEmailSuppressed,
        updatedAt: new Date(),
      }
      await setDoc(ref, merged)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactsQueryKey })
      showSuccess('Marketing suppress updated')
    },
    onError: () => showError('Could not update marketing suppress'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db.contacts, id))
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactsQueryKey })
      showSuccess('Contact deleted')
    },
    onError: () => showError('Could not delete contact'),
  })

  const linkPortalUserMutation = useMutation({
    mutationFn: async ({ contactId, userId }: LinkContactPortalUserInput) => {
      const ref = doc(db.contacts, contactId)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Contact not found')
      const trimmedUserId = userId.trim()
      if (!trimmedUserId) throw new Error('Invalid user id')
      const now = new Date()
      const merged: Contact = {
        ...current,
        userId: trimmedUserId,
        updatedAt: now,
      }
      await setDoc(ref, merged)
      await rematchSoldVehiclesToContact(contactId, trimmedUserId)
      return merged
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contactsQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['master_inventory'] }),
      ])
      showSuccess('Client account linked to contact')
    },
    onError: () => showError('Could not link client account'),
  })

  const mergeMutation = useMutation({
    mutationFn: async (input: MergeContactsInput) => {
      if (input.primaryContactId === input.secondaryContactId) {
        throw new Error('Choose two different contacts to merge.')
      }

      const primaryRef = doc(db.contacts, input.primaryContactId)
      const secondaryRef = doc(db.contacts, input.secondaryContactId)
      const [primarySnap, secondarySnap] = await Promise.all([
        getDoc(primaryRef),
        getDoc(secondaryRef),
      ])
      const primary = primarySnap.data()
      const secondary = secondarySnap.data()
      if (!primary) throw new Error('Primary contact not found')
      if (!secondary) throw new Error('Secondary contact not found')

      const merged = buildMergedContact(
        primary,
        secondary,
        input.fieldSources,
        input.secondaryEmailOverride,
        input.primaryEmailOverride,
      )
      await setDoc(primaryRef, merged)

      const [companiesSnapshot, tasksSnapshot] = await Promise.all([
        getDocs(db.contact_companies),
        getDocs(db.tasks),
      ])

      await Promise.all(
        companiesSnapshot.docs.map(async (companyDoc) => {
          const company = companyDoc.data()
          const referencesSecondaryMember = company.members.some(
            (member) => member.contactId === input.secondaryContactId,
          )
          const referencesSecondaryActivity = (company.activities ?? []).some(
            (activity) => activity.contactId === input.secondaryContactId,
          )
          if (!referencesSecondaryMember && !referencesSecondaryActivity) return
          const nextMembers = remapCompanyMembersForContactMerge(
            company.members,
            input.secondaryContactId,
            input.primaryContactId,
          )
          const nextActivities = (company.activities ?? []).map((activity) =>
            activity.contactId === input.secondaryContactId
              ? { ...activity, contactId: input.primaryContactId }
              : activity,
          )
          await setDoc(companyDoc.ref, {
            ...company,
            members: nextMembers,
            activities: nextActivities,
            updatedAt: new Date(),
          })
        }),
      )

      await Promise.all(
        tasksSnapshot.docs.map(async (taskDoc) => {
          const task = taskDoc.data()
          if (task.contact_id !== input.secondaryContactId) return
          await updateDoc(taskDoc.ref, {
            contact_id: input.primaryContactId,
            updatedAt: new Date(),
          })
        }),
      )

      await remapContactIdsAfterContactMerge(
        input.secondaryContactId,
        input.primaryContactId,
        secondary.userId,
      )

      await deleteDoc(secondaryRef)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contactsQueryKey }),
        queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['inventory_leads'] }),
        queryClient.invalidateQueries({ queryKey: ['master_inventory'] }),
      ])
      showSuccess('Contacts merged')
    },
    onError: (error: Error) => {
      showError(error.message || 'Could not merge contacts')
    },
  })

  const logActivityMutation = useMutation({
    mutationFn: async (input: LogContactActivityInput) => {
      const uid = userInfo?.id
      if (!uid) throw new Error('You must be signed in.')
      const ref = doc(db.contacts, input.contactId)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Contact not found')
      const now = new Date()
      const newActivity: ContactActivity = {
        type: input.type,
        note: input.note?.trim() ?? '',
        ...(input.taskId ? { taskId: input.taskId } : {}),
        ...(input.leadId ? { leadId: input.leadId } : {}),
        domain: input.domain === 'inventory' ? 'inventory' : 'sales',
        createdAt: now,
        createdByUserId: uid,
      }
      const bumpLastActivity = activityBumpsLastActivityAt(newActivity.domain)
      const merged: Contact = {
        ...current,
        activities: [...(current.activities ?? []), newActivity],
        ...(bumpLastActivity ? { lastActivityAt: now } : {}),
        updatedAt: now,
      }
      await setDoc(ref, merged)
      return { contactId: input.contactId, contact: merged, activity: newActivity }
    },
    onSuccess: (result) => {
      // Patch caches immediately so Save does not wait on a full contacts refetch
      // (invalidateQueries(['contacts']) matches list + detail + by_ids and was ~10–15s).
      queryClient.setQueryData<Contact[]>(contactsQueryKey, (prev) => {
        if (!prev) return prev
        return sortContactsForList(
          prev.map((c) => (c.id === result.contactId ? result.contact : c)),
        )
      })
      queryClient.setQueryData<Contact | null>(['contacts', result.contactId], result.contact)
      void queryClient.invalidateQueries({ queryKey: contactsQueryKey, exact: true })
      showSuccess('Activity logged')
    },
    onError: () => showError('Could not log activity'),
  })

  return {
    contacts: contacts ?? [],
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    createContact: (input: CreateContactInput) => createMutation.mutateAsync(input),
    updateContact: (input: UpdateContactInput) => updateMutation.mutateAsync(input),
    setContactMarketingEmailStatus: (id: string, marketingEmailStatus: MarketingEmailStatus) =>
      setMarketingEmailStatusMutation.mutateAsync({ id, marketingEmailStatus }),
    setContactMarketingEmailSuppressed: (id: string, marketingEmailSuppressed: boolean) =>
      setMarketingEmailSuppressedMutation.mutateAsync({ id, marketingEmailSuppressed }),
    deleteContact: (id: string) => deleteMutation.mutateAsync(id),
    logContactActivity: (input: LogContactActivityInput) =>
      logActivityMutation.mutateAsync(input),
    linkContactPortalUser: (input: LinkContactPortalUserInput) =>
      linkPortalUserMutation.mutateAsync(input),
    mergeContacts: (input: MergeContactsInput) => mergeMutation.mutateAsync(input),
    isSaving: createMutation.isPending || updateMutation.isPending,
    isUpdatingMarketingEmailStatus: setMarketingEmailStatusMutation.isPending,
    isUpdatingMarketingEmailSuppressed: setMarketingEmailSuppressedMutation.isPending,
    isLinkingPortalUser: linkPortalUserMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isMerging: mergeMutation.isPending,
    isLoggingActivity: logActivityMutation.isPending,
  }
}
